import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Row, Col, Button, Tag, Modal, Form, Input, Select, DatePicker,
  Spin, Typography, Space, Timeline, Badge, Tooltip, Popconfirm
} from 'antd';
import {
  PlusOutlined, CommentOutlined, ClockCircleOutlined, UserOutlined,
  DeleteOutlined, LockOutlined, CalendarOutlined, FilterOutlined,
  SearchOutlined, TeamOutlined, ShoppingCartOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/auth';
import { internalTaskApi, InternalTask, TaskDetail, TaskUser } from '../services/internalTaskApi';
import api from '../services/api';
import {
  CustomerOptionDto,
  TaskOrderOptionDto,
  normalizeCustomerOptions,
  normalizeTaskOrderOptions,
} from '../dto/reference';
import { canDeleteInternalTask } from '../modules/tasks/internalTaskAccessPolicy';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import { formatDateEuropean, formatDateTimeEuropean } from '../utils/dateUtils';
import '../components/layout/AppPage.css';
import '../components/layout/ObjectCard.css';
import './InternalTasks.css';

const { TextArea } = Input;
const { Text, Title } = Typography;

const STATUSES = ['NEW', 'IN_PROGRESS', 'REVIEW', 'DONE', 'CANCELLED'];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

const STATUS_COLORS: Record<string, string> = {
  NEW: 'default',
  IN_PROGRESS: 'processing',
  REVIEW: 'warning',
  DONE: 'success',
  CANCELLED: 'error'
};

const PRIORITY_COLORS: Record<string, string> = {
  LOW: 'default',
  MEDIUM: 'blue',
  HIGH: 'orange',
  URGENT: 'red'
};

const InternalTasks: React.FC = () => {
  const { t } = useTranslation(['tasks', 'common']);
  const feedback = useFeedback();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState<InternalTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [createForm] = Form.useForm();
  const [commentForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // Reference data
  const [users, setUsers] = useState<TaskUser[]>([]);
  const [customers, setCustomers] = useState<CustomerOptionDto[]>([]);
  const [orders, setOrders] = useState<TaskOrderOptionDto[]>([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterPriority, setFilterPriority] = useState<string[]>([]);
  const [filterAssignee, setFilterAssignee] = useState<string | undefined>(undefined);
  const [filterCustomer, setFilterCustomer] = useState<string | undefined>(undefined);
  const [filterOrder, setFilterOrder] = useState<string | undefined>(undefined);
  const [filterDueDate, setFilterDueDate] = useState<string | undefined>(undefined);
  const [filterSearch, setFilterSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const user = authService.getCurrentUser();

  // Fetch reference data once
  useEffect(() => {
    Promise.all([
      internalTaskApi.getUsers().then(setUsers).catch(() => {}),
      api.get('/customers').then(r => setCustomers(normalizeCustomerOptions(r.data, 'tasks.customers'))).catch(() => {}),
      api.get('/orders').then(r => setOrders(normalizeTaskOrderOptions(r.data, 'tasks.orders'))).catch(() => {})
    ]);
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      const filters: Record<string, string> = {};
      if (filterStatus.length) filters.status = filterStatus.join(',');
      if (filterPriority.length) filters.priority = filterPriority.join(',');
      if (filterAssignee) filters.assigneeId = filterAssignee;
      if (filterCustomer) filters.customerId = filterCustomer;
      if (filterOrder) filters.orderId = filterOrder;
      if (filterDueDate) filters.dueDate = filterDueDate;
      if (filterSearch.trim()) filters.search = filterSearch.trim();

      const data = await internalTaskApi.getTasks(filters);
      setTasks(data);
    } catch (error) {
      console.error('Failed to fetch internal tasks:', error);
      feedback.error(t('tasks:messages.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority, filterAssignee, filterCustomer, filterOrder, filterDueDate, filterSearch, t]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const tasksByStatus = useMemo(() => {
    const grouped: Record<string, InternalTask[]> = {};
    STATUSES.forEach(s => { grouped[s] = []; });
    tasks.forEach(task => {
      if (grouped[task.status]) {
        grouped[task.status].push(task);
      }
    });
    return grouped;
  }, [tasks]);

  // Filters behind the toggle; the title search stays in the toolbar.
  const advancedFilterCount = [
    filterStatus.length > 0, filterPriority.length > 0, filterAssignee, filterCustomer, filterOrder, filterDueDate,
  ].filter(Boolean).length;
  const hasActiveFilters = advancedFilterCount > 0 || Boolean(filterSearch.trim());

  const clearFilters = () => {
    setFilterStatus([]);
    setFilterPriority([]);
    setFilterAssignee(undefined);
    setFilterCustomer(undefined);
    setFilterOrder(undefined);
    setFilterDueDate(undefined);
    setFilterSearch('');
  };

  const handleCreateTask = async (values: any) => {
    setSubmitting(true);
    try {
      await internalTaskApi.createTask({
        title: values.title,
        description: values.description,
        priority: values.priority || 'MEDIUM',
        visibility: values.visibility || 'PUBLIC',
        assigneeId: values.assigneeId || null,
        customerId: values.customerId || null,
        orderId: values.orderId || null,
        dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : null
      });
      feedback.success(t('tasks:messages.createSuccess', { title: values.title }));
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchTasks();
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'tasks:messages.createError', undefined, { title: values.title }));
    } finally {
      setSubmitting(false);
    }
  };

  const openTaskDetail = async (taskId: string) => {
    setDetailLoading(true);
    setDetailModalVisible(true);
    try {
      const task = await internalTaskApi.getTask(taskId);
      setSelectedTask(task);
    } catch (error) {
      feedback.error(t('tasks:messages.detailFetchError'));
      setDetailModalVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // Open a task directly from a notification deep link (/app/tasks?task=<id>),
  // then drop the param so a manual close/refresh does not reopen it.
  useEffect(() => {
    const deepLinkTaskId = searchParams.get('task');
    if (deepLinkTaskId) {
      openTaskDetail(deepLinkTaskId);
      searchParams.delete('task');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    // Every call site passes selectedTask.id, so selectedTask (if set) always
    // names the task being changed here.
    const title = selectedTask?.title || '';
    try {
      await internalTaskApi.updateStatus(taskId, newStatus);
      feedback.success(t('tasks:messages.statusChanged', { title, status: t(`tasks:statuses.${newStatus}`) }));
      fetchTasks();
      if (selectedTask?.id === taskId) {
        openTaskDetail(taskId);
      }
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'tasks:messages.statusChangeError', undefined, { title }));
    }
  };

  const handleAddComment = async (values: any) => {
    if (!selectedTask) return;
    setSubmitting(true);
    try {
      await internalTaskApi.addComment(selectedTask.id, values.content);
      feedback.success(t('tasks:messages.commentAdded', { title: selectedTask.title }));
      commentForm.resetFields();
      openTaskDetail(selectedTask.id);
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'tasks:messages.commentError', undefined, { title: selectedTask.title }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const title = selectedTask?.title || '';
    try {
      await internalTaskApi.deleteTask(taskId);
      feedback.success(t('tasks:messages.deleteSuccess', { title }));
      setDetailModalVisible(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'tasks:messages.deleteError', undefined, { title }));
    }
  };

  // Fixed Polish format (DD.MM.RRRR GG:MM), whatever the browser language.
  const formatDate = (dateStr: string) => formatDateTimeEuropean(dateStr);

  // Parse YYYY-MM-DD as local date (not UTC) to avoid timezone shift. The API
  // may instead send a full timestamp of the stored day ("2026-10-16T22:00:00.000Z"
  // for 17 October in Warsaw); that one is read in the browser's time zone.
  const parseLocalDate = (dateStr: string) => {
    if (dateStr.length > 10) {
      const stamp = new Date(dateStr);
      return new Date(stamp.getFullYear(), stamp.getMonth(), stamp.getDate());
    }
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const isDueDateOverdue = (dueDate: string | null) => {
    if (!dueDate) return false;
    const due = parseLocalDate(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due < today;
  };

  if (loading) {
    return <Spin size="large" className="tasks-loading" />;
  }

  const isOverdueOpen = (task: { dueDate: string | null; status: string }) =>
    isDueDateOverdue(task.dueDate) && task.status !== 'DONE' && task.status !== 'CANCELLED';

  return (
    <div className="app-page tasks-page">
      <section className="app-page__card">
        <div className="app-page__toolbar">
          <Input
            className="app-page__toolbar-search"
            placeholder={t('tasks:filters.search')}
            prefix={<SearchOutlined />}
            value={filterSearch}
            onChange={e => setFilterSearch(e.target.value)}
            allowClear
          />
          <div className="app-page__toolbar-actions">
            <Badge count={advancedFilterCount} size="small" offset={[-2, 2]}>
              <Button
                icon={<FilterOutlined />}
                type={filtersOpen || advancedFilterCount > 0 ? 'primary' : 'default'}
                ghost={filtersOpen || advancedFilterCount > 0}
                onClick={() => setFiltersOpen(open => !open)}
              >
                {t('tasks:filters.title')}
              </Button>
            </Badge>
            {hasActiveFilters && (
              <Button type="link" onClick={clearFilters}>
                {t('tasks:filters.clear')}
              </Button>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
              {t('tasks:newTask')}
            </Button>
          </div>
        </div>

        {filtersOpen && (
          <Row gutter={[12, 12]} className="app-page__filters">
            <Col xs={12} md={8} lg={4}>
              <Select
                mode="multiple"
                className="tasks-filter"
                aria-label={t('tasks:filters.status')}
                placeholder={t('tasks:filters.status')}
                value={filterStatus}
                onChange={setFilterStatus}
                maxTagCount={1}
                allowClear
              >
                {STATUSES.map(s => (
                  <Select.Option key={s} value={s}>
                    <Tag color={STATUS_COLORS[s]}>{t(`tasks:statuses.${s}`)}</Tag>
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} md={8} lg={4}>
              <Select
                mode="multiple"
                className="tasks-filter"
                aria-label={t('tasks:filters.priority')}
                placeholder={t('tasks:filters.priority')}
                value={filterPriority}
                onChange={setFilterPriority}
                maxTagCount={1}
                allowClear
              >
                {PRIORITIES.map(p => (
                  <Select.Option key={p} value={p}>
                    <Tag color={PRIORITY_COLORS[p]}>{t(`tasks:priorities.${p}`)}</Tag>
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} md={8} lg={4}>
              <Select
                className="tasks-filter"
                aria-label={t('tasks:filters.assignee')}
                placeholder={t('tasks:filters.assignee')}
                value={filterAssignee}
                onChange={setFilterAssignee}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {users.map(u => (
                  <Select.Option key={u.id} value={u.id}>{u.name}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} md={8} lg={4}>
              <Select
                className="tasks-filter"
                aria-label={t('tasks:filters.customer')}
                placeholder={t('tasks:filters.customer')}
                value={filterCustomer}
                onChange={setFilterCustomer}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {customers.map(c => (
                  <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} md={8} lg={4}>
              <Select
                className="tasks-filter"
                aria-label={t('tasks:filters.order')}
                placeholder={t('tasks:filters.order')}
                value={filterOrder}
                onChange={setFilterOrder}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.searchText ?? '').includes(input.toLowerCase())
                }
              >
                {orders.map(o => (
                  <Select.Option key={o.id} value={o.id} searchText={o.searchText}>
                    {o.label || o.orderNumber}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} md={8} lg={4}>
              <Select
                className="tasks-filter"
                aria-label={t('tasks:filters.dueDate')}
                placeholder={t('tasks:filters.dueDate')}
                value={filterDueDate}
                onChange={setFilterDueDate}
                allowClear
              >
                <Select.Option value="overdue">{t('tasks:filters.dueDateOptions.overdue')}</Select.Option>
                <Select.Option value="today">{t('tasks:filters.dueDateOptions.today')}</Select.Option>
                <Select.Option value="this_week">{t('tasks:filters.dueDateOptions.thisWeek')}</Select.Option>
                <Select.Option value="no_date">{t('tasks:filters.dueDateOptions.noDate')}</Select.Option>
              </Select>
            </Col>
          </Row>
        )}
      </section>

      {/* Board: one equal column per status, as on „Tablica” (DESIGN.md §3C). */}
      <div className="tasks-board">
        {STATUSES.map(status => (
          <section key={status} className="tasks-board__column" aria-label={t(`tasks:columns.${status}`)}>
            <header className="tasks-board__head">
              <span className={`tasks-board__dot tasks-board__dot--${status.toLowerCase()}`} aria-hidden="true" />
              <span className="tasks-board__name">{t(`tasks:columns.${status}`)}</span>
              <span className="tasks-board__count">{tasksByStatus[status].length}</span>
            </header>
            <div className="tasks-board__body">
              {tasksByStatus[status].length === 0 ? (
                <div className="tasks-board__empty">{t('tasks:noTasks')}</div>
              ) : (
                tasksByStatus[status].map(task => (
                  <button
                    key={task.id}
                    type="button"
                    className="tasks-card"
                    onClick={() => openTaskDetail(task.id)}
                  >
                    <span className="tasks-card__top">
                      <Tag color={PRIORITY_COLORS[task.priority]}>
                        {t(`tasks:priorities.${task.priority}`)}
                      </Tag>
                      {task.visibility === 'PRIVATE' && (
                        <Tooltip title={t('tasks:visibilityOptions.privateHint')}>
                          <LockOutlined className="tasks-muted-icon" />
                        </Tooltip>
                      )}
                    </span>

                    <span className="tasks-card__title" title={task.title}>{task.title}</span>

                    {(task.customerName || task.displayOrderNumber || task.orderNumber) && (
                      <span className="tasks-card__links">
                        {task.customerName && (
                          <Tag color="cyan" title={task.customerName}>
                            <TeamOutlined /> {task.customerName}
                          </Tag>
                        )}
                        {(task.displayOrderNumber || task.orderNumber) && (
                          <Tag color="geekblue">
                            <ShoppingCartOutlined /> {task.displayOrderNumber || task.orderNumber}
                          </Tag>
                        )}
                      </span>
                    )}

                    <span className="tasks-card__foot">
                      <span
                        className={task.assigneeName ? 'tasks-card__assignee' : 'tasks-card__assignee is-empty'}
                        title={task.assigneeName || t('tasks:unassigned')}
                      >
                        {task.assigneeName ? <><UserOutlined /> {task.assigneeName}</> : t('tasks:unassigned')}
                      </span>
                      <span className="tasks-card__facts">
                        {task.dueDate && (
                          <Tooltip title={t('tasks:dueDate')}>
                            <Tag color={isOverdueOpen(task) ? 'red' : undefined}>
                              <CalendarOutlined /> {formatDateEuropean(parseLocalDate(task.dueDate))}
                            </Tag>
                          </Tooltip>
                        )}
                        {task.commentCount > 0 && (
                          <Tooltip title={t('tasks:comments')}>
                            <span className="tasks-card__comments">
                              <CommentOutlined className="tasks-muted-icon" /> {task.commentCount}
                            </span>
                          </Tooltip>
                        )}
                      </span>
                    </span>
                  </button>
                ))
              )}
            </div>
          </section>
        ))}
      </div>

      {/* Create Task Modal */}
      <Modal
        title={t('tasks:newTask')}
        open={createModalVisible}
        onCancel={() => { setCreateModalVisible(false); createForm.resetFields(); }}
        maskClosable={false}
        footer={null}
        width={650}
        className="obj-drawer"
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreateTask} onFinishFailed={() => feedback.warning(t('tasks:messages.fillRequired'))} scrollToFirstError>
          <Form.Item
            name="title"
            label={t('tasks:taskTitle')}
            rules={[{ required: true, message: t('tasks:taskTitle') + ' is required' }]}
          >
            <Input placeholder={t('tasks:taskTitlePlaceholder')} />
          </Form.Item>

          <Form.Item name="description" label={t('tasks:taskDescription')}>
            <TextArea rows={3} placeholder={t('tasks:taskDescriptionPlaceholder')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="priority" label={t('tasks:priority')} initialValue="MEDIUM">
                <Select>
                  {PRIORITIES.map(p => (
                    <Select.Option key={p} value={p}>
                      <Tag color={PRIORITY_COLORS[p]}>{t(`tasks:priorities.${p}`)}</Tag>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="assigneeId" label={t('tasks:assignee')}>
                <Select placeholder={t('tasks:assigneePlaceholder')} allowClear showSearch optionFilterProp="children">
                  {users.map(u => (
                    <Select.Option key={u.id} value={u.id}>{u.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="dueDate" label={t('tasks:dueDate')}>
                <DatePicker className="tasks-filter" placeholder={t('tasks:dueDatePlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="customerId" label={t('tasks:customer')}>
                <Select placeholder={t('tasks:customerPlaceholder')} allowClear showSearch optionFilterProp="children">
                  {customers.map(c => (
                    <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="orderId" label={t('tasks:order')}>
                <Select
                  placeholder={t('tasks:orderPlaceholder')}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.searchText ?? '').includes(input.toLowerCase())
                  }
                >
                  {orders.map(o => (
                    <Select.Option key={o.id} value={o.id} searchText={o.searchText}>
                      {o.label || o.orderNumber}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="visibility" label={t('tasks:visibility')} initialValue="PUBLIC">
                <Select>
                  <Select.Option value="PUBLIC">{t('tasks:visibilityOptions.PUBLIC')}</Select.Option>
                  <Select.Option value="PRIVATE">
                    <Space><LockOutlined />{t('tasks:visibilityOptions.PRIVATE')}</Space>
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div className="tasks-modal-actions">
            <Button onClick={() => { setCreateModalVisible(false); createForm.resetFields(); }}>
              {t('common:cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('tasks:newTask')}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Task Detail Modal */}
      <Modal
        title={selectedTask?.title || t('tasks:taskDetail')}
        open={detailModalVisible}
        onCancel={() => { setDetailModalVisible(false); setSelectedTask(null); }}
        maskClosable={false}
        footer={null}
        width={700}
      >
        {detailLoading ? (
          <Spin className="tasks-detail__loading" />
        ) : selectedTask ? (
          <div className="tasks-detail">
            {/* Status + Priority + Actions */}
            <div className="tasks-detail__head">
              <Space>
                <Tag color={STATUS_COLORS[selectedTask.status]}>
                  {t(`tasks:statuses.${selectedTask.status}`)}
                </Tag>
                <Tag color={PRIORITY_COLORS[selectedTask.priority]}>
                  {t(`tasks:priorities.${selectedTask.priority}`)}
                </Tag>
                {selectedTask.visibility === 'PRIVATE' && (
                  <Tag icon={<LockOutlined />}>{t('tasks:visibilityOptions.PRIVATE')}</Tag>
                )}
              </Space>
              <Space>
                <Select
                  className="tasks-detail__status"
                  value={selectedTask.status}
                  onChange={(val) => handleStatusChange(selectedTask.id, val)}
                  size="small"
                  aria-label={t('tasks:filters.status')}
                >
                  {STATUSES.map(s => (
                    <Select.Option key={s} value={s}>
                      {t(`tasks:statuses.${s}`)}
                    </Select.Option>
                  ))}
                </Select>
                {canDeleteInternalTask(selectedTask, user) && (
                  <Popconfirm
                    title={t('tasks:deleteConfirm')}
                    onConfirm={() => { void handleDeleteTask(selectedTask.id); }}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
              </Space>
            </div>

            {/* Description */}
            {selectedTask.description && (
              <div className="tasks-detail__panel tasks-detail__text">{selectedTask.description}</div>
            )}

            {/* Meta info */}
            <dl className="tasks-detail__panel tasks-detail__facts">
              <div>
                <dt>{t('tasks:assignee')}</dt>
                <dd>{selectedTask.assigneeName || t('tasks:unassigned')}</dd>
              </div>
              <div>
                <dt>{t('tasks:createdBy')}</dt>
                <dd>{selectedTask.createdByName}</dd>
              </div>
              {selectedTask.customerName && (
                <div>
                  <dt>{t('tasks:customer')}</dt>
                  <dd>{selectedTask.customerName}</dd>
                </div>
              )}
              {(selectedTask.displayOrderNumber || selectedTask.orderNumber) && (
                <div>
                  <dt>{t('tasks:order')}</dt>
                  <dd>
                    <button
                      type="button"
                      className="tasks-link"
                      onClick={() => navigate(`/app/orders/${selectedTask.orderId}`)}
                    >
                      {selectedTask.displayOrderNumber || selectedTask.orderNumber}
                    </button>
                  </dd>
                </div>
              )}
              {selectedTask.dueDate && (
                <div>
                  <dt>{t('tasks:dueDate')}</dt>
                  <dd>
                    <Tag color={isOverdueOpen(selectedTask) ? 'red' : undefined}>
                      <CalendarOutlined /> {formatDateEuropean(parseLocalDate(selectedTask.dueDate))}
                    </Tag>
                    {isOverdueOpen(selectedTask) && (
                      <Text type="danger" className="tasks-detail__overdue">{t('tasks:overdue')}</Text>
                    )}
                  </dd>
                </div>
              )}
              <div>
                <dt>{t('tasks:createdAt')}</dt>
                <dd>{formatDate(selectedTask.createdAt)}</dd>
              </div>
            </dl>

            {/* Comments */}
            <Title level={5} className="tasks-detail__title">
              <CommentOutlined /> {t('tasks:comments')}
            </Title>

            {selectedTask.comments.length === 0 ? (
              <Text type="secondary" className="tasks-detail__empty">
                {t('tasks:noComments')}
              </Text>
            ) : (
              <div className="tasks-detail__comments">
                {selectedTask.comments.map(comment => (
                  <div key={comment.id} className="tasks-detail__comment">
                    <div className="tasks-detail__comment-head">
                      <span className="tasks-detail__comment-author">{comment.authorName}</span>
                      <span className="tasks-detail__meta">{formatDate(comment.createdAt)}</span>
                    </div>
                    <div className="tasks-detail__text">{comment.content}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Comment Form */}
            <Form form={commentForm} onFinish={handleAddComment} scrollToFirstError>
              <Form.Item name="content" rules={[{ required: true, message: '' }]}>
                <TextArea rows={2} placeholder={t('tasks:commentPlaceholder')} />
              </Form.Item>
              <div className="tasks-modal-actions tasks-modal-actions--start">
                <Button type="primary" htmlType="submit" loading={submitting} size="small">
                  {t('tasks:addComment')}
                </Button>
              </div>
            </Form>

            {/* Activity Timeline */}
            {selectedTask.activities && selectedTask.activities.length > 0 && (
              <>
                <Title level={5} className="tasks-detail__title tasks-detail__title--spaced">
                  <ClockCircleOutlined /> {t('tasks:activity')}
                </Title>
                <Timeline
                  items={selectedTask.activities.map(act => ({
                    children: (
                      <div>
                        <Text strong>{act.actorName}</Text>{' '}
                        <Text>{t(`tasks:actions.${act.action}`)}</Text>
                        {act.action === 'STATUS_CHANGED' && act.details && (
                          <Text type="secondary">
                            {' '}({t(`tasks:statuses.${act.details.from}`)} → {t(`tasks:statuses.${act.details.to}`)})
                          </Text>
                        )}
                        {act.action === 'ASSIGNED' && act.details?.to && (
                          <Text type="secondary">
                            {' '}→ {users.find(u => u.id === act.details.to)?.name || act.details.to}
                          </Text>
                        )}
                        <br />
                        <span className="tasks-detail__meta">
                          {formatDate(act.createdAt)}
                        </span>
                      </div>
                    )
                  }))}
                />
              </>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default InternalTasks;
